import { useEffect } from 'react';
import { useUnreadCounts } from '@/api/community-hooks';
import { useNotificationPreferences } from '@/api/hooks';

const BASE_TITLE = 'Alfie';

function applyTitle(count: number) {
  if (typeof document === 'undefined') return;
  const next = count > 0 ? `(${count}) ${BASE_TITLE}` : BASE_TITLE;
  if (document.title !== next) document.title = next;
}

/**
 * Slack-style document title: `(N) Alfie` while there are unread chat messages,
 * `Alfie` when caught up. Mirrors the sidebar chat badge (DMs + community
 * channels) — same source, same semantics.
 *
 * Inbox notifications are intentionally excluded: notify-community writes both
 * a chat message AND an inbox notification for every new message, so summing
 * them double-counts. The inbox bell has its own indicator.
 *
 * The count drops naturally when the user opens a conversation and the read
 * receipt updates the unread cache — no special focus/visibility handling.
 */
export function useDocumentTitleUnread() {
  const { data: unreadCounts } = useUnreadCounts();
  const { data: prefs } = useNotificationPreferences();

  useEffect(() => {
    if (prefs && prefs.tab_title_enabled === false) {
      applyTitle(0);
      return;
    }

    const dmUnread = (prefs && !prefs.direct_messages)
      ? 0
      : (unreadCounts?.filter((c) => c.type === 'dm').reduce((sum, c) => sum + c.count, 0) ?? 0);
    const communityUnread = (prefs && !prefs.channel_messages)
      ? 0
      : (unreadCounts
          ?.filter((c) => c.type === 'community_channel' || c.type === 'group')
          .reduce((sum, c) => sum + c.count, 0) ?? 0);

    applyTitle(dmUnread + communityUnread);
  }, [unreadCounts, prefs]);

  // Reset to base on unmount (e.g. logout)
  useEffect(() => () => applyTitle(0), []);
}
